import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const ApprovalLinesContext = createContext();

export const ApprovalLinesProvider = ({ children }) => {
  const [approvalLines, setApprovalLines] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    include: "user,approver", // Assuming the API supports including related user data
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const fetchApprovalLines = useCallback(async () => {
    setApprovalLines((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/approval-lines${queryString}`);
      if (response.success) {
        setApprovalLines({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setApprovalLines({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchApprovalLines();
  }, [fetchApprovalLines]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = approvalLines.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const Pagination = () => PaginationComponent;

  return (
    <ApprovalLinesContext.Provider
      value={{
        approvalLines,
        filterOptions,
        handleSearch,
        refetch: fetchApprovalLines,
        Pagination,
      }}
    >
      {children}
    </ApprovalLinesContext.Provider>
  );
};

export const useApprovalLines = () => {
  const context = useContext(ApprovalLinesContext);
  if (!context) {
    throw new Error(
      "useApprovalLines must be used within ApprovalLinesProvider"
    );
  }

  return context;
};
